import type { State } from '@hookstate/core'
import { Box, IconButton, InputAdornment, TextField } from '@mui/material'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import React from 'react'
import { useLang } from '~/hooks/useLang'

interface FieldTextRegistrationProps {
  readonly disableFields: boolean
  readonly value: string | number | null
  readonly onChangeValue: (value) => void
  readonly label: string
  readonly type?: string
  readonly float?: boolean
  readonly inputErrors?: State<any>
  readonly multiline?: boolean
  readonly rows?: number
  readonly height?: number
  readonly required?: boolean
  readonly onError?: (value: boolean) => void
  readonly error?: boolean
  readonly endAdormendProps?:JSX.Element
}

const ALLOWED_KEYS = new Set([
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
  'Home', 'End',
])

export default function FieldTextRegistration({
  disableFields,
  value,
  label,
  type = 'text',
  inputErrors,
  multiline = false,
  rows = 2,
  height = 50,
  required = false,
  float,
  endAdormendProps,
  onError,
  onChangeValue,
}: FieldTextRegistrationProps) {
  const { translate } = useLang()
  const isNumericType = type === 'number' || type === 'float'

  const step = float ? 0.01 : 1
  const min = float ? -100 : 0
  const max = label === 'Age' ? 110 : 100000

  const handleStep = (direction: 'up' | 'down') => {
    const current = parseFloat(String(value ?? 0).replace(',', '.')) || 0
    const next = direction === 'up' ? current + step : current - step
    const clamped = Math.min(max, Math.max(min, next))
    const formatted = float ? parseFloat(clamped.toFixed(2)) : Math.round(clamped)
    onChangeValue(String(formatted))
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isNumericType) return
    if (ALLOWED_KEYS.has(event.key)) return
    if (event.ctrlKey || event.metaKey) return
    if (event.key >= '0' && event.key <= '9') return
    if (event.key === '-') return
    if (float && (event.key === '.')) return
    event.preventDefault()
  }

  const handleValueChange = (currentValue: string) => {
    onChangeValue(currentValue)

    if (inputErrors) {
      if (!currentValue.length && required) {
        inputErrors.set(true)
      } else {
        inputErrors.set(false)
      }
    }
  }

  return (
    <TextField
      id={`outlined-${label}`}
      fullWidth
      required={required}
      multiline={multiline}
      rows={multiline ? rows : undefined}
      size="small"
      type="text"
      error={!!inputErrors?.get()}
      disabled={disableFields}
      label={translate(label)}
      value={value ?? ''}
      onKeyDown={handleKeyDown}
      onChange={(event) => {
        const currentValue = event.target.value
        handleValueChange(currentValue)
      }}
      onFocus={() => {
        if (!value && required && onError) {
          onError(true)
        }
      }}
      onBlur={() => {
        if (!value && required && onError) {
          onError(true)
        }
      }}
      InputProps={{
        inputProps: {
          inputMode: isNumericType ? (float ? 'decimal' : 'numeric') : undefined,
          pattern: isNumericType ? (float ? '[0-9]*\\.?[0-9]*' : '[0-9]*') : undefined,
        },
        endAdornment: isNumericType ? (
          <InputAdornment position="end">
            {endAdormendProps}
            <Box sx={{ display: 'flex', flexDirection: 'column', ml: 0.5 }}>
              <IconButton
                size="small"
                disabled={disableFields}
                onClick={() => handleStep('up')}
                sx={{ padding: 0, height: 14, width: 14 }}
              >
                <KeyboardArrowUpIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <IconButton
                size="small"
                disabled={disableFields}
                onClick={() => handleStep('down')}
                sx={{ padding: 0, height: 14, width: 14 }}
              >
                <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          </InputAdornment>
        ) : endAdormendProps ? endAdormendProps : null,
      }}
      sx={{ height: multiline ? height : 'auto' }}
    />
  )
}

